/**
 * Générateur — Automatisme : écrire l'égalité de Pythagore dans un triangle
 * rectangle.
 * Attendu ID : 1243 | Niveau : 3ème | Domaine : Espace et géométrie, Triangles
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 l. 1097-1101 ; reprend
 * l'objectif 4e l. 1044 sans gen en base).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * GEN NEUF : une figure (figTriangle.js, sept orientations, angle droit codé
 * à n'importe quelle lettre du nom, lettres saines), l'égalité SEULE. Hors :
 * calculer une longueur (151), la réciproque (190), la racine (1220), la
 * trigonométrie. Le texte dit « rectangle en A » ET la figure le code
 * (À ARBITRER : codage seul). Banque MathsMentales 4GB1 relevée (égalité,
 * sept figures, lettres tirées).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « L'égalité de Pythagore » (egalite_qcm_unique) : « KMT est
 *     rectangle en M. Quelle égalité est vraie ? » [KT² = KM² + MT² ;
 *     KM² = KT² + MT² ; MT² = KM² + KT² ; KT = KM + MT] (jamais les deux
 *     ordres de la clé).
 *   Carte 2 « Écris l'égalité » (egalite_saisie) : « KMT est rectangle en
 *     M. Écris l'égalité de Pythagore » en SAISIE LIBRE, comparaison
 *     d'expression (décision prof 13/09, « c'est commutatif ») : KT^2 = KM^2 +
 *     MT^2 et toutes ses écritures équivalentes admises (ordre des carrés,
 *     membres échangés, KT ou TK, ² ou ^2 ou 2 collé) ; le corrigé écrit KT².
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : l'égalité, l'hypoténuse, le
 *     côté le plus long, l'angle droit.
 *
 * INVARIANTS : les côtés sont nommés dans l'ordre alphabétique de leurs
 * lettres (KM, KT, MT) ; la figure porte data-figure et data-angle-droit ;
 * distracteurs distincts ; toute lettre en KaTeX.
 * SACRIFIÉ (fiche « ABC rectangle en A, BC² = AB² + AC² ») : le triple
 * {A, B, C} dans tout ordre, jamais tiré ; auto-testé.
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   hypotenuse_au_mauvais_cote : KM² = KT² + MT² (un côté de l'angle droit isolé), PRINCIPALE, deux variantes ;
 *   sans_carres                : KT = KM + MT ;
 *   soustraction               : KT² = KM² − MT² ;
 *   produit_pour_somme         : KT² = KM² × MT² ;
 *   sommet_pour_cote           : une lettre seule dans l'égalité (tuiles K, M, T).
 */
import {
    randomChoice, randInt, shuffle, pickLettresSains, tex,
    makeQcm, makeVraiFaux, makeSaisie,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { svgTriangle, triangleRectangle, ORIENTATIONS } from './figTriangle';

const ATTENDU_ID = 1243;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_QCM = 'L\'égalité de Pythagore';
const L_SAISIE = 'Écris l\'égalité';
const L_VF = 'Vrai ou faux';

const POOL = 'ABCDEFGHJKLMNPRSTUV'.split('');
/** Le triple sacrifié (fiche) : A, B, C dans tout ordre. */
export const SACRIFIE = ['A', 'B', 'C'];
const estSacrifie = (lettres) => [...lettres].sort().join('') === 'ABC';

const EXPLICATIONS = {
    regle: 'Dans un triangle rectangle, le carré du côté le plus long (l\'hypoténuse, en face de l\'angle droit) est égal à la somme des carrés des deux autres côtés.',
    hypotenuse_au_mauvais_cote: 'Le côté seul dans l\'égalité, c\'est l\'hypoténuse : celui qui est en face de l\'angle droit, le plus long.',
    sans_carres: 'Les longueurs ne s\'ajoutent pas : ce sont leurs carrés qui s\'ajoutent.',
    soustraction: 'Le carré de l\'hypoténuse est la SOMME des deux autres carrés.',
    produit_pour_somme: 'On ajoute les carrés, on ne les multiplie pas.',
    sommet_pour_cote: 'Dans l\'égalité, on écrit des longueurs de côtés (deux lettres), pas des sommets.',
};

/** Un triangle rectangle nommé : lettres saines hors {A, B, C}, angle droit à l'une des trois, une des sept orientations. */
const tirer = () => {
    let lettres;
    for (let essai = 0; essai < 50; essai++) { lettres = pickLettresSains(POOL, 3); if (!estSacrifie(lettres)) break; }
    const droit = randInt(0, 2);
    const a = randInt(3, 6); const b = randInt(2, 5);
    const scene = triangleRectangle({ lettres, droit, a: a + (a === b ? 1 : 0), b, deg: randomChoice(ORIENTATIONS) });
    const D = lettres[droit];
    const autres = lettres.filter((l) => l !== D);
    const cote = (p, q) => [p, q].sort().join('');
    return { lettres, D, hyp: cote(autres[0], autres[1]), c1: cote(D, autres[0]) < cote(D, autres[1]) ? cote(D, autres[0]) : cote(D, autres[1]), c2: cote(D, autres[0]) < cote(D, autres[1]) ? cote(D, autres[1]) : cote(D, autres[0]), scene, nom: lettres.join('') };
};
const figure = (t) => imgElement(svgTriangle(t.scene, `Le triangle ${t.nom}, rectangle en ${t.D}, avec son angle droit codé.`), 'large');
const intro = (t) => `<p>${tex(t.nom)} est un triangle rectangle en ${tex(t.D)}.</p>`;
const eg = (h, x, y, op = '+', carres = true) => (carres ? tex(`${h}^{2} = ${x}^{2} ${op} ${y}^{2}`) : tex(`${h} = ${x} + ${y}`));

// ─── Carte 1 : l'égalité (QCM) ────────────────────────────────────────────

const genQcm = () => {
    const t = tirer();
    const bon = eg(t.hyp, t.c1, t.c2);
    const autres = shuffle([
        { v: eg(t.hyp, t.c1, t.c2, '+', false), code: 'sans_carres' },
        { v: eg(t.hyp, t.c1, t.c2, '-'), code: 'soustraction' },
        { v: eg(t.hyp, t.c1, t.c2, '\\times'), code: 'produit_pour_somme' },
    ]);
    const faux = [
        { v: eg(t.c1, t.hyp, t.c2), code: 'hypotenuse_au_mauvais_cote' },
        { v: eg(t.c2, t.c1, t.hyp), code: 'hypotenuse_au_mauvais_cote' },
        autres[0],
    ];
    const payload = makeQcm([bon, ...faux.map((f) => f.v)], bon, `${intro(t)}<p><b>Quelle égalité est vraie ?</b></p>`, { ...opts(L_QCM), elements_additionnels: figure(t) });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { lettres: t.lettres, D: t.D, hyp: t.hyp, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 2 : écris l'égalité (saisie libre, comparaison d'expression) ──

/**
 * Les écritures admises de l'égalité (décision prof 13/09, « c'est commutatif ») :
 * les deux ordres des carrés à droite, les deux membres échangés, chaque côté
 * nommé dans un sens ou l'autre (BE ou EB), l'exposant « ² » (la touche du
 * clavier, décision prof 13/09), « ^2 » ou « 2 » collé. La première est la
 * canonique (BE² = …), celle du corrigé.
 */
export const ecrituresPythagore = (hyp, c1, c2) => {
    const sens = (ab) => [ab, `${ab[1]}${ab[0]}`];
    const out = [];
    for (const h of sens(hyp)) for (const a of sens(c1)) for (const b of sens(c2)) {
        for (const exp of ['\u00b2', '^2', '2']) {
            const H = `${h}${exp}`; const A = `${a}${exp}`; const B = `${b}${exp}`;
            for (const e of [`${H} = ${A} + ${B}`, `${H} = ${B} + ${A}`, `${A} + ${B} = ${H}`, `${B} + ${A} = ${H}`]) if (!out.includes(e)) out.push(e);
        }
    }
    return out;
};

const genSaisie = () => {
    const t = tirer();
    const ecritures = ecrituresPythagore(t.hyp, t.c1, t.c2);
    const payload = makeSaisie(ecritures[0], `${intro(t)}<p><b>Écris l'égalité de Pythagore.</b></p>`, {
        ...opts(L_SAISIE), strict: true, comparaison: 'expression', reponses_acceptees: ecritures, elements_additionnels: figure(t),
    });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { lettres: t.lettres, D: t.D, hyp: t.hyp, c1: t.c1, c2: t.c2, distracteurs: [{ code: 'hypotenuse_au_mauvais_cote' }, { code: 'sans_carres' }] };
    return payload;
};

// ─── Carte 3 : Vrai ou faux ───────────────────────────────────────────────

const genVf = () => {
    const t = tirer();
    const autreCote = randomChoice([t.c1, t.c2]);
    const autreSommet = randomChoice(t.lettres.filter((l) => l !== t.D));
    const candidats = [
        { l: eg(t.hyp, t.c1, t.c2), v: true, fam: 'egalite' },
        { l: eg(autreCote, t.hyp, autreCote === t.c1 ? t.c2 : t.c1), v: false, fam: 'egalite', code: 'hypotenuse_au_mauvais_cote' },
        { l: eg(t.hyp, t.c1, t.c2, '+', false), v: false, fam: 'egalite', code: 'sans_carres' },
        { l: `L'hypoténuse est ${tex(`[${t.hyp}]`)}.`, v: true, fam: 'hypotenuse' },
        { l: `L'hypoténuse est ${tex(`[${autreCote}]`)}.`, v: false, fam: 'hypotenuse', code: 'hypotenuse_au_mauvais_cote' },
        { l: `${tex(`[${t.hyp}]`)} est le côté le plus long.`, v: true, fam: 'plus_long' },
        { l: `${tex(`[${autreCote}]`)} est le côté le plus long.`, v: false, fam: 'plus_long', code: 'hypotenuse_au_mauvais_cote' },
        { l: `L'angle droit est en ${tex(t.D)}.`, v: true, fam: 'angle' },
        { l: `L'angle droit est en ${tex(autreSommet)}.`, v: false, fam: 'angle', code: 'hypotenuse_au_mauvais_cote' },
    ];
    for (let essai = 0; essai < 100; essai++) {
        const nbFaux = randInt(1, 3);
        const fausses = shuffle(candidats.filter((x) => !x.v)); const vraies = shuffle(candidats.filter((x) => x.v));
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((x) => x.fam)).size !== 4) continue;
        const payload = makeVraiFaux(lignes.map((x) => x.l), lignes.map((x) => (x.v ? 0 : 1)), `${intro(t)}<p><b>Vrai ou faux ?</b></p>`, { ...opts(L_VF), elements_additionnels: figure(t) });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { lettres: t.lettres, D: t.D, hyp: t.hyp, distracteurs: lignes.filter((x) => x.code).map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_1243 : V/F sans tirage');
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'lettres saines hors {A, B, C} (sacrifié), angle droit à n\'importe quelle lettre, sept orientations, côtés nommés dans l\'ordre alphabétique, tout en KaTeX',
        'jamais les deux ordres de la clé (KT² = KM² + MT² et KT² = MT² + KM²) dans un même QCM',
    ],
    egalite_qcm_unique: 'hypotenuse_au_mauvais_cote (deux variantes, la principale) + un parmi sans_carres, soustraction, produit_pour_somme',
    egalite_saisie: 'saisie libre en comparaison d\'expression : les deux ordres des carrés, les deux membres, chaque côté dans un sens ou l\'autre, ² ou ^2 ou 2 collé ; toute autre égalité (hypoténuse au mauvais côté, sans carrés) refusée',
    sens_tableau_vf: 'quatre familles exclusives : l\'égalité, l\'hypoténuse, le côté le plus long, l\'angle droit',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    egalite_qcm_unique: genQcm,
    egalite_saisie: genSaisie,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'egalite', label: L_QCM, formatsList: [{ key: 'egalite_qcm_unique', label: 'QCM' }] },
    { id: 'ecrire', label: L_SAISIE, formatsList: [{ key: 'egalite_saisie', label: 'Saisie' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
